import {
  Component,
  createMemo,
  createResource,
  For,
  Match,
  onCleanup,
  Show,
  Switch,
} from 'solid-js';
import {
  RunDetailsSelection,
  useApiServices,
  useDataContext,
} from '@mobile-mapping/providers';
import { useConnectProject } from '@trimble/connect-project-providers';
import {
  BtnClasses,
  IconButton,
  PanelLayout,
  Spinner,
  WithLabel,
} from '@tcweb/connect-ui';
import { t } from '@trimble/i18n';
import {
  formatBytes,
  formatCount,
  formatPointCloudArea,
  joinParts,
  toMedDateTimeString,
} from '@trimble/utilities';
import type { Pointcloud } from '@data-access/geospatial/scan-data';
import { Camera } from '@data-access/geospatial/mobile-mapping';
import { ErrorState } from '../components/LoadingState';
import { LoadState } from '@trimble/data-loading';

export interface RunDetailsProps {
  readonly run: RunDetailsSelection;
  onClose(): void;
}

function formatLocation(location: Pointcloud['location']): string {
  if (!location) return '';
  return joinParts([
    location.country,
    location.stateName ?? location.state,
    location.city,
    location.streetAddress,
    location.zip,
  ]);
}

export const RunDetails: Component<RunDetailsProps> = (props) => {
  const { mobileMappingApi, scanDataApi } = useApiServices();
  const { dataState } = useDataContext();
  const { selectedTenantTrn, selectedRegion, selectedProject } =
    useConnectProject();

  const missionTrn = (): string => props.run.missionTrn;
  const runId = (): string => props.run.runId;

  const imageDerivedStationCount = createMemo((): number | undefined => {
    if (dataState.mission.state !== LoadState.LoadComplete) {
      return undefined;
    }
    const mission = Object.values(dataState.mission.items).find(
      (m) => m.missionTrn === missionTrn()
    );
    const run = mission?.runs.find((r) => r.runId === runId());
    return run?.numberOfStations;
  });

  const stationCountDisplayText = createMemo((): string => {
    const fromMission = imageDerivedStationCount();
    if (typeof fromMission === 'number') {
      return String(fromMission);
    }
    return t('mobileMapping.runDetails.stationCountKnownAfterRunLoaded');
  });

  const stationCountIsPending = createMemo(
    (): boolean => typeof imageDerivedStationCount() !== 'number'
  );

  /* eslint-disable solid/reactivity -- Query hook handles reactivity internally */
  const camerasQuery = mobileMappingApi.listCamerasReactiveQuery(
    selectedTenantTrn,
    missionTrn,
    runId
  );
  /* eslint-enable solid/reactivity */

  const cameras = createMemo((): Camera[] => {
    if (!camerasQuery.isSuccess) return [];
    return (camerasQuery.data.items ?? [])
      .slice()
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true })
      );
  });

  const [pointCloudData] = createResource(
    () => {
      const region = selectedRegion();
      const projectId = selectedProject()?.id;
      const pcTrn = props.run.id;
      if (!region || !projectId || !pcTrn) return undefined;
      return { region, projectId, pcTrn };
    },
    async (params) => {
      const controller = new AbortController();
      onCleanup(() => controller.abort());
      return scanDataApi.getPointCloudQuery(
        params.region,
        params.projectId,
        params.pcTrn,
        controller.signal
      );
    }
  );

  return (
    <PanelLayout
      panelTitle={props.run.name}
      rightActions={
        <IconButton
          color={BtnClasses.TERTIARY}
          icon="tc-icon-content-close"
          size="icon-medium"
          data-cy="run-details-close"
          clicked={() => props.onClose()}
        />
      }
      content={
        <div data-testid="run-details" class="contents">
          <Switch>
            <Match when={pointCloudData.loading}>
              <div data-testid="run-details-spinner" class="w-full h-[300px]">
                <Spinner show={true} />
              </div>
            </Match>
            <Match when={pointCloudData.error}>
              <div class="p-3" data-testid="run-details-error">
                <ErrorState />
              </div>
            </Match>
            <Match when={pointCloudData()} keyed>
              {(pc) => (
                <>
                  <div class="flex flex-col p-3">
                    <div data-testid="run-details-title" class="h5">
                      {t('mobileMapping.runDetails.title')}
                    </div>

                    <Show when={pc.captureDate} keyed>
                      {(value) => (
                        <WithLabel
                          label={t('mobileMapping.runDetails.captureDate')}
                          name=""
                        >
                          {toMedDateTimeString(new Date(value))}
                        </WithLabel>
                      )}
                    </Show>

                    <Show when={pc.uploadedBy} keyed>
                      {(value) => (
                        <WithLabel
                          label={t('mobileMapping.runDetails.uploadedBy')}
                          name=""
                        >
                          {value}
                        </WithLabel>
                      )}
                    </Show>

                    <Show when={pc.uploadedDate} keyed>
                      {(value) => (
                        <WithLabel
                          label={t('mobileMapping.runDetails.uploadDate')}
                          name=""
                        >
                          {toMedDateTimeString(new Date(value))}
                        </WithLabel>
                      )}
                    </Show>

                    <Show when={pc.scannerType} keyed>
                      {(value) => (
                        <WithLabel
                          label={t('mobileMapping.runDetails.scannerType')}
                          name=""
                        >
                          {value}
                        </WithLabel>
                      )}
                    </Show>

                    <WithLabel
                      label={t('mobileMapping.runDetails.numberOfStations')}
                      name=""
                    >
                      <span
                        data-testid="run-details-station-count"
                        class={
                          stationCountIsPending() ? 'text-gray-6' : undefined
                        }
                      >
                        {stationCountDisplayText()}
                      </span>
                    </WithLabel>

                    <Show when={pc.name} keyed>
                      {(value) => (
                        <WithLabel
                          label={t('mobileMapping.runDetails.pointCloudName')}
                          name=""
                        >
                          <span class="break-words">{value}</span>
                        </WithLabel>
                      )}
                    </Show>

                    <Show
                      when={pc.pointCount != null ? pc.pointCount : false}
                      keyed
                    >
                      {(value) => (
                        <WithLabel
                          label={t('mobileMapping.runDetails.pointCount')}
                          name=""
                        >
                          {formatCount(value)}
                        </WithLabel>
                      )}
                    </Show>

                    <WithLabel
                      label={t('mobileMapping.runDetails.classified')}
                      name=""
                    >
                      {pc.isClassified ? t('general.yes') : t('general.no')}
                    </WithLabel>

                    <Show when={pc.area != null ? pc.area : false} keyed>
                      {(value) => (
                        <WithLabel
                          label={t('mobileMapping.runDetails.pointCloudArea')}
                          name=""
                        >
                          {formatPointCloudArea(value)}
                        </WithLabel>
                      )}
                    </Show>

                    <Show when={pc.location} keyed>
                      {(value) => (
                        <WithLabel
                          label={t(
                            'mobileMapping.runDetails.pointCloudLocation'
                          )}
                          name=""
                        >
                          <span class="break-words">
                            {formatLocation(value)}
                          </span>
                        </WithLabel>
                      )}
                    </Show>

                    <Show
                      when={pc.fileSize != null ? pc.fileSize : false}
                      keyed
                    >
                      {(value) => (
                        <WithLabel
                          label={t('mobileMapping.runDetails.size')}
                          name=""
                        >
                          {formatBytes(value)}
                        </WithLabel>
                      )}
                    </Show>

                    <Show when={pc.notes} keyed>
                      {(value) => (
                        <WithLabel
                          label={t('mobileMapping.runDetails.notes')}
                          name=""
                        >
                          <span class="break-words">{value}</span>
                        </WithLabel>
                      )}
                    </Show>
                  </div>

                  <Show when={cameras().length > 0}>
                    <div class="border-t border-[#d0d0d7]">
                      <div class="flex flex-col p-3">
                        <div class="h5">
                          {t('mobileMapping.runDetails.cameras')}
                        </div>
                        <For each={cameras()}>
                          {(camera) => (
                            <div class="flex flex-col py-1">
                              <span class="text-sm break-words">
                                {camera.name}
                              </span>
                              <div class="flex gap-2 text-xs text-gray-6">
                                <Show when={camera.isPanorama}>
                                  <span>
                                    {t('mobileMapping.runDetails.panorama')}
                                  </span>
                                </Show>
                                <Show
                                  when={
                                    camera.offsetFromGround != null &&
                                    camera.offsetFromGround > 0
                                  }
                                >
                                  <span>
                                    {t(
                                      'mobileMapping.runDetails.offsetFromGround'
                                    )}
                                    : {camera.offsetFromGround} m
                                  </span>
                                </Show>
                              </div>
                            </div>
                          )}
                        </For>
                      </div>
                    </div>
                  </Show>
                </>
              )}
            </Match>
          </Switch>
        </div>
      }
    />
  );
};
